import 'core-js';
import 'regenerator-runtime/runtime';
import 'raf/polyfill'; // required by React 16
import React from 'react';
import ReactDOM from 'react-dom';
import { ConnectedRouter } from 'connected-react-router';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import * as Sentry from '@sentry/browser';
import Auth from '@aws-amplify/auth';
import { LocaleObject, setLocale } from 'yup';
import './scss/index.scss';
import moment from 'moment';
import * as FullStory from '@fullstory/browser';
import {
  detectCookies,
  detectIE,
  detectLanguage,
  detectLowerCaseINS,
  handleFontFamilies,
  site,
} from '@whitelabel/helpers/site';
import { loadCountries } from '@whitelabel/helpers/locales';
import { loadLocale } from '@whitelabel/actions';
import { CURRENT_LOCALE, USER_CONTINENT, USER_COUNTRY } from '@whitelabel/helpers/constants';
import languages from '@whitelabel/helpers/languages';
import theme from '@whitelabel/global-theme';
import { GlobalStyle } from '@whitelabel/component-library';
import { captureExceptionWithFullyStory } from '@whitelabel/helpers/utils';
import { yupMessages } from '@whitelabel/helpers/forms/messages';
import loadPolyfills from './polyfills';
import { rootSaga } from './actions';
import configureStore, { history, sagaMiddleware } from './store';
import { loadMessages } from './helpers/locales';
import App from './containers/App';
import NoCookiesErrorPage from './components/NoCookiesErrorPage';
import ErrorPage from './components/ErrorPage';
import UnsupportedBrowserPage from './components/UnsupportedBrowserPage';
import CustomIntlProvider from './components/CustomIntlProvider';
import { storeUserGeoIpInfo } from './helpers/utils';

const bootstrapApp = async () => {
  const {
    NODE_ENV,
    REACT_APP_SENTRY_DSN: SENTRY_DSN,
    REACT_APP_COGNITO_REGION: COGNITO_REGION,
    REACT_APP_COGNITO_USER_POOL_ID: COGNITO_USER_POOL_ID,
    REACT_APP_COGNITO_IDENTITY_POOL_ID: COGNITO_IDENTITY_POOL_ID,
    REACT_APP_COGNITO_APP_CLIENT_ID: COGNITO_APP_CLIENT_ID,
  } = process.env;
  try {
    detectLowerCaseINS();
    const isCookiesEnabled = detectCookies();

    if (isCookiesEnabled && (!sessionStorage.getItem(USER_COUNTRY) || !sessionStorage.getItem(USER_CONTINENT))) {
      await storeUserGeoIpInfo();
    }

    //we move detectLanguage here because it need the country code be stored first
    detectLanguage();
    const { locale } = site;
    const isIE = detectIE();
    await loadPolyfills();

    Sentry.init({
      dsn: SENTRY_DSN,
      attachStacktrace: true,
      ignoreErrors: [/(\$|jQuery) is not defined/i, /Can't find variable: (\$|jQuery)/i],
    });

    if (!window.isHeadless) {
      FullStory.init({ orgId: '34DW0', devMode: NODE_ENV === 'development' });
    }

    const { bidi } = languages[locale as keyof typeof languages];
    let messages;
    let fetchMessagesError = false;
    try {
      messages = await loadMessages(locale);
    } catch (error) {
      captureExceptionWithFullyStory(error);
      messages = {};
      fetchMessagesError = true;
    }

    const intl = {
      locale,
      messages,
      bidi,
    };

    if (bidi) {
      const RTLStyles = document.createElement('link');
      RTLStyles.setAttribute('rel', 'stylesheet');
      RTLStyles.setAttribute('href', 'https://cdn.jsdelivr.net/npm/bootstrap@5.0.2/dist/css/bootstrap.rtl.min.css');
      RTLStyles.setAttribute('integrity', 'sha384-gXt9imSW0VcJVHezoNQsP+TNrjYXoGcrqBZJpry9zJt8PCQjobwmhMGaDHTASo9N');
      RTLStyles.setAttribute('crossorigin', 'anonymous');
      document.head.appendChild(RTLStyles);
    }

    const store = configureStore({
      intl,
    });
    sagaMiddleware.run(rootSaga);

    let component;
    if (isCookiesEnabled && !isIE && !fetchMessagesError) {
      Auth.configure({
        mandatorySignIn: true,
        region: COGNITO_REGION,
        userPoolId: COGNITO_USER_POOL_ID,
        identityPoolId: COGNITO_IDENTITY_POOL_ID,
        userPoolWebClientId: COGNITO_APP_CLIENT_ID,
      });
      await loadLocale(locale);
      site.countries = await loadCountries(locale);

      setLocale(yupMessages as LocaleObject);
      sessionStorage.setItem(CURRENT_LOCALE, locale);
      if (locale === 'ar') {
        moment.updateLocale('ar', {
          weekdaysMin: 'الأحد_الإثنين_الثلاثاء_الأربعاء_الخميس_الجمعة_السبت'.split('_'),
          preparse: (string: string) => string,
          postformat: (string: string) => string,
        });
      }
      component = <App />;
    } else if (isIE) {
      component = <UnsupportedBrowserPage />;
    } else if (fetchMessagesError) {
      component = (
        <ErrorPage
          title="Fetch messages failed"
          button={{
            label: 'Reload',
            onClick: () => window.location.reload(),
          }}
        />
      );
    } else {
      component = <NoCookiesErrorPage />;
    }

    handleFontFamilies(locale, theme);

    ReactDOM.render(
      <Provider store={store}>
        <CustomIntlProvider locale={locale}>
          <ThemeProvider theme={theme}>
            <ConnectedRouter history={history}>
              <GlobalStyle />
              {component}
            </ConnectedRouter>
          </ThemeProvider>
        </CustomIntlProvider>
      </Provider>,
      document.getElementById('root'),
    );
  } catch (error) {
    captureExceptionWithFullyStory(error);
    if (NODE_ENV === 'development') {
      throw error;
    }
  }
};

bootstrapApp();
